import { isExternalWriteup } from '../utils';
import { useEffect, useRef, useState } from 'react';
import { ImageIcon, Loader2, Pencil } from 'lucide-react';
import { z } from 'zod';

import { MarkdownEditor, MarkdownEditorRef } from 'components/blocks/markdown-editor';
import { Button } from 'components/ui/button';
import { AssistantAttachmentPreview } from 'features/chat/components';
import { MarkdownRenderer } from 'features/chat/agent-chat-module/components/markdown-renderer/markdown-renderer';
import {
  IKnowledgeBaseItem,
  KBType,
} from 'features/chat/agent-chat-module/types/kb.service.type';
import { useUpdateKB } from 'features/chat/hooks/use-kb';
import { Attachment, MessageRole } from 'features/chat/types/chat';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';

const TEXT_MIN_LENGTH = 100;
const TEXT_MAX_LENGTH = 10000;

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.svg', '.bmp', '.ico', '.tiff', '.avif'];

function isImageKbItem(kb?: IKnowledgeBaseItem | null): boolean {
  if (!kb || kb.kb_type !== KBType.FILE || !kb.file) return false;
  const name = (kb.file.name || kb.file.system_name || '').toLowerCase();
  const hasImageExt = IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
  const hasPhotoTag = kb.file.content?.trim().toLowerCase().startsWith('[photo]');
  return hasImageExt || Boolean(hasPhotoTag);
}

const textKbFormSchema = z.object({
  text: z
    .string()
    .trim()
    .min(TEXT_MIN_LENGTH, `At least ${TEXT_MIN_LENGTH} characters is required`)
    .max(TEXT_MAX_LENGTH, `Maximum ${TEXT_MAX_LENGTH} characters is allowed`),
});

const answerKbFormSchema = z
  .string()
  .trim()
  .max(TEXT_MAX_LENGTH, `Maximum ${TEXT_MAX_LENGTH} characters is allowed`)
  .refine((val) => val.replace(/\s+/g, ' ').trim().length >= TEXT_MIN_LENGTH, {
    message: `At least ${TEXT_MIN_LENGTH} characters is required`,
  });

type KbDocumentContentProps = {
  kb?: IKnowledgeBaseItem;
  canEdit?: boolean;
};

const documentPreviewProseClass = cn(
  'max-w-none',
  'prose-headings:font-semibold prose-headings:text-foreground prose-headings:tracking-tight',
  'prose-h1:mb-5 prose-h1:mt-0 prose-h1:text-2xl prose-h1:font-bold',
  'prose-h2:mb-4 prose-h2:mt-6 prose-h2:text-lg',
  'prose-h3:mb-3 prose-h3:mt-5 prose-h3:text-base',
  'prose-p:mb-3 prose-p:mt-0 prose-p:text-sm prose-p:leading-7 prose-p:text-foreground',
  'prose-strong:font-semibold prose-strong:text-foreground',
  'prose-a:text-primary prose-a:font-normal prose-a:underline prose-a:underline-offset-2 hover:text-primary/80',
  'prose-li:text-sm prose-li:leading-7 prose-li:text-foreground',
  'prose-ul:my-3 prose-ol:my-3',
  'prose-hr:my-6 prose-hr:border-border'
);

function getEditableContent(kb: IKnowledgeBaseItem): string {
  switch (kb.kb_type) {
    case KBType.FILE:
      return kb.file?.content || '';
    case KBType.TEXT:
      return kb.text?.content || '';
    case KBType.LINK:
      return kb.link?.content || '';
    case KBType.QNA:
      return kb.qa?.answer || '';
    default:
      return '';
  }
}

function getPreviewMarkdown(kb: IKnowledgeBaseItem, content: string): string {
  if (kb.kb_type === KBType.QNA && kb.qa) {
    return `**Question**\n\n${kb.qa.question}\n\n**Answer**\n\n${content}`;
  }
  return content;
}

function hasLengthConstraints(kbType?: KBType): boolean {
  return kbType === KBType.TEXT || kbType === KBType.QNA;
}

function getNormalizedLength(value: string): number {
  return value.replace(/\s+/g, ' ').trim().length;
}

export function KbDocumentContent({ kb, canEdit = true }: KbDocumentContentProps) {
  canEdit = canEdit && !isExternalWriteup(kb);
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const { toast } = useToast();

  const [content, setContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const editorRef = useRef<MarkdownEditorRef>(null);
  const { mutateAsync: updateKb } = useUpdateKB(kb?.kb_type || KBType.TEXT);

  const isImage = isImageKbItem(kb);
  const file: Attachment | null = kb?.file
    ? {
      fileId: String(kb.file.file_id || ''),
      fileName: kb.file.name || kb.file.system_name || 'image',
      extension: (kb.file.name || kb.file.system_name || '').split('.').pop()?.toLowerCase() || '',
      sender: MessageRole.ASSISTANT,
    }
    : null;

  useEffect(() => {
    if (kb) {
      setContent(getEditableContent(kb));
    } else {
      setContent('');
    }
    setIsEditing(false);
  }, [kb]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const validateContent = (value: string): string | undefined => {
    if (kb?.kb_type === KBType.QNA) {
      const result = answerKbFormSchema.safeParse(value);
      if (!result.success) {
        return result.error.errors[0]?.message;
      }
      return undefined;
    }

    if (kb?.kb_type === KBType.TEXT) {
      const result = textKbFormSchema.safeParse({ text: value });
      if (!result.success) {
        return result.error.errors[0]?.message;
      }
    }

    return undefined;
  };

  const handleSave = async () => {
    if (!canEdit || isSaving || !kb || !projectKey) return;

    const validationError = editorRef.current?.validate();
    if (validationError) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await updateKb({
        content: content.trim(),
        type: kb.kb_type,
        kb_id: kb.id,
        project_key: projectKey,
        question: kb.kb_type === KBType.QNA ? kb.qa?.question || '' : undefined,
      });

      if (!response.is_success) {
        throw new Error(response.detail || 'Failed to update document content.');
      }

      toast({
        variant: 'success',
        title: 'Content updated',
        description: 'The document content was saved successfully.',
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: 'Unable to save content',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!kb) return;
    setContent(getEditableContent(kb));
    setIsEditing(false);
  };

  const handleEditStart = () => {
    if (!canEdit) return;
    setIsEditing(true);
    setTimeout(() => {
      editorRef.current?.focus();
    }, 100);
  };

  const isSaveDisabled = () => {
    if (isSaving || !kb) return true;
    if (hasLengthConstraints(kb.kb_type)) {
      const cleanLength = getNormalizedLength(content);
      return cleanLength < TEXT_MIN_LENGTH || cleanLength > TEXT_MAX_LENGTH;
    }
    return false;
  };

  const previewContent = kb ? getPreviewMarkdown(kb, content) : '';
  const imageDescription = previewContent;
  const showLengthConstraints = hasLengthConstraints(kb?.kb_type);

  if (!kb) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-sm font-semibold text-foreground">Preview</h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 md:px-8 md:py-8">
          <p className="text-sm text-muted-foreground">No document selected.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <DocumentHeader
        isEditing={isEditing}
        isSaving={isSaving}
        isSaveDisabled={isSaveDisabled()}
        canEdit={canEdit}
        onEdit={handleEditStart}
        onCancel={handleCancel}
        onSave={handleSave}
      />

      <div className="min-h-0 flex-1 overflow-y-auto bg-card">
        {isEditing ? (
          <div className="flex h-full min-h-0 flex-col px-6 py-6 md:px-8 md:py-8">
            {kb.kb_type === KBType.QNA && kb.qa && (
              <div className="mb-4 shrink-0">
                <p className="mb-1 text-sm font-medium text-foreground">Question</p>
                <p className="mb-4 text-sm text-muted-foreground">{kb.qa.question}</p>
                <p className="mb-2 text-sm font-medium text-foreground">Answer</p>
              </div>
            )}
            <div className="min-h-0 flex-1">
              <MarkdownEditor
                ref={editorRef}
                value={content}
                onChange={setContent}
                minLength={showLengthConstraints ? TEXT_MIN_LENGTH : undefined}
                maxLength={showLengthConstraints ? TEXT_MAX_LENGTH : undefined}
                showCharacterCount={showLengthConstraints}
                validate={validateContent}
                className="min-h-[320px]"
                placeholder="Enter your knowledge base content..."
                disabled={isSaving}
                showToolbar
                showModeToggle
                defaultMode="edit"
              />
            </div>
          </div>
        ) : (
          <div className="px-6 py-6 md:px-8 md:py-8">
            {isImage && file ? (
              <div className="space-y-6">
                <AssistantAttachmentPreview key={file.fileId} file={file} />

                {imageDescription ? (
                  <div className="space-y-2">
                    <MarkdownRenderer
                      content={imageDescription}
                      className={documentPreviewProseClass}
                    />
                  </div>
                ) : null}
              </div>
            ) : !previewContent.trim() ? (
              <p className="text-sm text-muted-foreground">
                No preview content is available for this document yet.
              </p>
            ) : (
              <MarkdownRenderer content={previewContent} className={documentPreviewProseClass} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type DocumentHeaderProps = {
  isEditing: boolean;
  isSaving: boolean;
  isSaveDisabled: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
};

function DocumentHeader({
  isEditing,
  isSaving,
  isSaveDisabled,
  canEdit,
  onEdit,
  onCancel,
  onSave,
}: DocumentHeaderProps) {
  return (
    <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
      <h2 className="text-sm font-semibold text-foreground">Preview</h2>
      {canEdit ? (
        !isEditing ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 px-2 text-sm font-normal text-muted-foreground hover:text-foreground"
            onClick={onEdit}
          >
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={onSave} disabled={isSaveDisabled}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}

